import { useState, useEffect, useRef, useCallback } from 'react';
import { openSseStream, SseStream, SseStreamStatus } from '../utils/sseStream';

interface ChatbotSSEMessage {
  type: 'chat_message' | 'chat_status' | 'mcp_operation' | 'connection';
  data: any;
  timestamp?: string;
}

interface UseChatbotSSEOptions {
  userId?: string;
  organizationId?: string;
  sessionId?: string;
  enabled?: boolean;
  onMessage?: (message: ChatbotSSEMessage) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

interface UseChatbotSSEReturn {
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastMessage: ChatbotSSEMessage | null;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 3000;

/**
 * Live chatbot stream.
 *
 * This used to append the raw login access token as `?token=` alongside
 * userId/organizationId. The server never read that token - it trusted the
 * userId/organizationId query params outright, so anyone who knew a victim's
 * ids received their entire conversation. It now requires a short-lived token
 * minted by GET /sse/token, and reads userId/organizationId out of it, so the
 * login token must not be sent here: it lacks the fields the stream token
 * carries and is rejected.
 *
 * openSseStream does the minting, and re-mints on every reconnect - the token
 * is only valid for 60 seconds, so a reconnect reusing the original one would
 * fail. sessionId stays a query param: it narrows which open chat session to
 * follow and is not what authorises the connection.
 */
export const useChatbotSSE = (options: UseChatbotSSEOptions): UseChatbotSSEReturn => {
  const {
    userId,
    organizationId,
    sessionId,
    enabled = true,
    onMessage,
    onError,
    onOpen,
    onClose
  } = options;

  const [status, setStatus] = useState<SseStreamStatus>('disconnected');
  const [lastMessage, setLastMessage] = useState<ChatbotSSEMessage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<SseStream | null>(null);

  // Consumers pass inline callbacks that change identity every render; holding
  // them in a ref keeps `connect` stable so the effect below cannot loop.
  const handlersRef = useRef({ onMessage, onError, onOpen, onClose });
  handlersRef.current = { onMessage, onError, onOpen, onClose };

  const disconnect = useCallback(() => {
    streamRef.current?.close();
    streamRef.current = null;
    setStatus('disconnected');
    setError(null);
  }, []);

  const emit = useCallback(
    (type: ChatbotSSEMessage['type'], raw: string) => {
      try {
        const message: ChatbotSSEMessage = {
          type,
          data: JSON.parse(raw),
          timestamp: new Date().toISOString()
        };
        setLastMessage(message);
        handlersRef.current.onMessage?.(message);
      } catch (err) {
        console.error(`Error parsing chatbot SSE ${type}:`, err);
      }
    },
    []
  );

  const connect = useCallback(() => {
    if (!enabled || !userId || !organizationId) {
      console.warn('Cannot connect to chatbot SSE: missing required parameters');
      return;
    }

    // Close any existing connection first.
    streamRef.current?.close();
    streamRef.current = null;

    streamRef.current = openSseStream({
      path: '/sse/chatbot',
      organizationId,
      params: { sessionId },
      onMessage: (event: MessageEvent) => emit('connection', event.data),
      listeners: {
        chat_message: (event: MessageEvent) => emit('chat_message', event.data),
        chat_status: (event: MessageEvent) => emit('chat_status', event.data),
        mcp_operation: (event: MessageEvent) => emit('mcp_operation', event.data)
      },
      onOpen: () => {
        setError(null);
        handlersRef.current.onOpen?.();
      },
      onStatusChange: setStatus,
      onError: (err, willRetry) => {
        setError(
          willRetry
            ? 'Connection error occurred'
            : 'Connection failed after multiple attempts'
        );
        handlersRef.current.onError?.(err as Event);
        if (!willRetry) {
          handlersRef.current.onClose?.();
        }
      },
      maxRetries: MAX_RECONNECT_ATTEMPTS,
      retryDelayMs: RECONNECT_DELAY_MS
    });
  }, [enabled, userId, organizationId, sessionId, emit]);

  // Effect to manage connection based on enabled state and dependencies
  useEffect(() => {
    if (enabled && userId && organizationId) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [enabled, userId, organizationId, connect, disconnect]);

  // Handle page visibility changes to reconnect when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && enabled && userId && organizationId) {
        // Reconnect when page becomes visible if not already connected
        if (status === 'disconnected' || status === 'error') {
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, userId, organizationId, status, connect]);

  return {
    status,
    lastMessage,
    error,
    connect,
    disconnect
  };
};
